import Card from '../../../components/card/Card';
import Spinner from '../../../components/Spinner/spinner';
import Button from '../../../components/button/Button';
import { useState, useEffect } from 'react';
import PostBalanceSheet from './PostBalanceSheet';
import {
  getPostBalanceItems,
  updatePostBalanceItems,
  getBalanceSheetsByCuit,
  getBalanceInfoByCode,
} from '../../../services/RiskService';
import { Toast, useToast } from '../../../components/toast/toast';
import P from '../../../components/texts/p/P';
import { RISK_CODES, APPLICATION_STATUS } from '../../../utils/Constants';
import Modal from '../../../components/modal/modal';
import TextArea from '../../../components/textarea/TextArea';
import { updateApplication, getApplication } from '../../../services/ApplicationService';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  navigateAfterFlowAction,
  type DetailLocationState,
} from '../../../utils/listNavigationState';
import useStatus from '../../../hooks/useStatus';
import useRole from '../../../hooks/useRole';
import { toNumber, hasData } from '../../../utils/Utils';
import { BalancePeriod, SalesPurchasesByMonth } from '../../../types/interfaces';
import { useConfig } from '../../../context/ConfigContext';
import { useFiscalIdConfig } from '../../../hooks/useFiscalIdConfig';
import { shouldShowPostBalanceCloudSearch } from './postBalanceCloudSearch';

/** Obtiene el período del balance (to_year, to_month) para la aplicación. Coerción numérica para evitar strings del API. */
async function getBalanceForPostBalance(
  cuit: string,
  applicationId: string
): Promise<{ to_year: number; to_month: number } | null> {
  const applicationResponse = await getApplication(cuit, applicationId);
  if (!hasData(applicationResponse) || !applicationResponse.data) return null;
  const balanceSheetCode = applicationResponse.data.balance_sheet_code as string | undefined;
  let balance: { to_year: number; to_month: number } | null = null;
  if (balanceSheetCode) {
    try {
      const balanceByCodeResponse = await getBalanceInfoByCode(cuit, balanceSheetCode);
      if (hasData(balanceByCodeResponse) && balanceByCodeResponse.data) {
        const b = balanceByCodeResponse.data;
        balance = { to_year: Number(b.to_year), to_month: Number(b.to_month) };
      }
    } catch {
      // fallback abajo
    }
  }
  if (!balance) {
    const balancesResponse = await getBalanceSheetsByCuit(cuit);
    if (!hasData(balancesResponse) || !balancesResponse.data?.length) return null;
    const b = balancesResponse.data[0];
    balance = { to_year: Number(b.to_year), to_month: Number(b.to_month) };
  }
  return balance;
}

interface PostBalanceData {
  sales?: Array<{ month: number; year: number; amount: number }>;
  purchases?: Array<{ month: number; year: number; amount: number }>;
  [key: string]: unknown;
}

interface PostBalanceActivityProps {
  applicationId: string | undefined;
  cuit: string | undefined;
  handleNextStep: () => void;
  handleRiskData: (codes: string[]) => void;
  purchasesSalesData: unknown;
  postBalanceData: PostBalanceData;
  setPostBalanceData: (data: PostBalanceData) => void;
  applicationStatus: number;
  balanceLastMonth: BalancePeriod | null | undefined;
}

const PostBalanceActivity: React.FC<PostBalanceActivityProps> = ({
  applicationId,
  cuit,
  handleNextStep,
  handleRiskData,
  purchasesSalesData: _purchasesSalesData,
  postBalanceData,
  setPostBalanceData,
  applicationStatus,
  balanceLastMonth
}) => {
  const { isInCommittee } = useStatus({
    applicationStatus,
  });
  const appConfig = useConfig();
  const fiscalId = useFiscalIdConfig();
  const showCloudSearch = shouldShowPostBalanceCloudSearch(
    appConfig?.isUruguayEnabled,
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const [purchasesData, setPurchasesData] = useState<Array<{ month: number; year: number; amount: number }>>([]);
  const [salesData, setSalesData] = useState<Array<{ month: number; year: number; amount: number }>>([]);
  const [validateForms, setValidateForms] = useState<boolean | null>(null);
  const [isCloudLoading, setIsCloudLoading] = useState<boolean>(false);
  const [cloudData, setCloudData] = useState<{ sales?: SalesPurchasesByMonth; purchases?: SalesPurchasesByMonth }>({});
  const [fetchedBalancePeriod, setFetchedBalancePeriod] = useState<{ to_year: number; to_month: number } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as DetailLocationState;
  const { canTransitionRisk } = useRole();
  const [salesValid, setSalesValid] = useState(false);
  const [purchasesValid, setPurchasesValid] = useState(false);

  // Cargar período del balance al montar para que el dropdown de meses tenga datos (aunque balanceInfo del padre aún no esté)
  useEffect(() => {
    if (!cuit || !applicationId) return;
    let cancelled = false;
    getBalanceForPostBalance(cuit, applicationId).then((period) => {
      if (!cancelled && period) setFetchedBalancePeriod(period);
    }).catch(() => {
      // silenciar; el usuario puede usar "Buscar en la nube" después
    });
    return () => { cancelled = true; };
  }, [cuit, applicationId]);

  const fetchPostBalanceItems = async () => {
    if (!cuit || !applicationId) {
      useToast.error({
        title: 'Error',
        id: 'post-balance-activity-toast',
        description: fiscalId.missingIdOrApplicationMessage,
        closeButton: true,
        autoClose: 5000,
      });
      return;
    }

    setIsCloudLoading(true);
    const c = cuit as string;
    const appId = applicationId as string;
    try {
      const period = await getBalanceForPostBalance(c, appId);
      if (!period) {
        useToast.error({
          title: 'No hay balance cargado',
          id: 'post-balance-activity-toast',
          description: 'Debe cargar un balance antes de obtener las ventas post balance',
          closeButton: true,
          autoClose: 5000,
        });
        setIsCloudLoading(false);
        return;
      }

      const toYear = period.to_year;
      const toMonth = period.to_month;
      const incrementMonth = (year: number, month: number) => {
        if (month === 12) {
          return { year: year + 1, month: '01' };
        }
        const newMonth = month + 1;
        return { year, month: newMonth < 10 ? `0${newMonth}` : `${newMonth}` };
      };
      const { year, month } = incrementMonth(toYear, toMonth);

      getPostBalanceItems(cuit!, applicationId!, String(year), month).then((response) => {
        if (response.status === 200 && hasData(response)) {
          setCloudData(response.data);
        } else {
          useToast.error({
            title: 'No hay ventas y compras posteriores al balance',
            id: 'post-balance-activity-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }
        setIsCloudLoading(false);
      }).catch(() => {
        useToast.error({
          title: 'Error al obtener las compras y ventas',
          id: 'post-balance-activity-toast',
          description: 'No se pudo conectar con el servidor. Verifica tu conexión.',
          closeButton: true,
          autoClose: 5000,
        });
        setIsCloudLoading(false);
      });
    } catch (error) {
      console.error('[PostBalanceActivity] Error en fetchPostBalanceItems:', error);
      useToast.error({
        title: 'Error al obtener el balance',
        id: 'post-balance-activity-toast',
        description: 'No se pudo obtener la información del balance',
        closeButton: true,
        autoClose: 5000,
      });
      setIsCloudLoading(false);
    }
  };

  const saveData = (shouldContinue: boolean) => {
    if (
      !purchasesData.some((item) => item.month === null) &&
      !salesData.some((item) => item.month === null)
    ) {
      setIsDisabled(true);
      setIsSaving(true);
      
      const payload = { purchases: purchasesData, sales: salesData };
      
      updatePostBalanceItems(
        cuit!,
        payload,
        applicationId!
      ).then((response) => {
        if (response.status === 200) {
          useToast.success({
            title: 'Guardado con éxito',
            id: 'post-balance-activity-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });

          if (shouldContinue) {
            handleRiskData([RISK_CODES.bpb]);
            handleNextStep();
          }
        } else {
          const res = response as { status: number; statusText?: string };
          useToast.error({
            title: 'Ocurrió un error al guardar la información.',
            id: 'post-balance-activity-toast',
            description: `Error ${res.status}: ${res.statusText ?? 'Error del servidor'}`,
            closeButton: true,
            autoClose: 5000,
          });
        }
        const resData = (response as { data?: { purchasesAnnualized?: number; salesAnnualized?: number; purchasesAverage?: number; salesAverage?: number } }).data;
        setPostBalanceData({
          purchases: purchasesData,
          sales: salesData,
          purchasesAnnualized: resData?.purchasesAnnualized ?? 0,
          salesAnnualized: resData?.salesAnnualized ?? 0,
          purchasesAverage: resData?.purchasesAverage ?? 0,
          salesAverage: resData?.salesAverage ?? 0,
        });
        setIsSaving(false);
        setIsDisabled(false);
      }).catch((_error) => {
        useToast.error({
          title: 'Error de conexión',
          id: 'post-balance-activity-toast',
          description: 'No se pudo conectar con el servidor. Verifica tu conexión.',
          closeButton: true,
          autoClose: 5000,
        });
        setIsSaving(false);
        setIsDisabled(false);
      });
    } else {
      validateForms === null
        ? setValidateForms(true)
        : setValidateForms((prev) => !prev);
    }
  };

  const handleSheetData = (
    e: { values: Array<{ month?: number | null; year?: number | null; amount?: { value?: unknown } }>; isValid: boolean },
    index: number
  ) => {
    const data: Array<{ month: number; year: number; amount: number }> = e.values.map((item) => {
      const raw = item.amount?.value;
      const amount = typeof raw === 'number' ? raw : typeof raw === 'string' ? toNumber(raw) : toNumber(0);
      return {
        month: item.month ?? 0,
        year: item.year ?? 0,
        amount,
      };
    });
    if (index === 1) {
      setPurchasesData(data);
      setPurchasesValid(e.isValid);
    } else {
      setSalesData(data);
      setSalesValid(e.isValid);
    }
  };

  const canSubmit = salesValid && purchasesValid && !isDisabled && !isSaving;

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSendingApplication(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, locationState);
        }, 1000);
      }
      setIsSendingApplication(false);
    });
  };

  return (
    <Card spacing={{ left: appConfig?.isUruguayEnabled ? '0' : '6' }}>
      <Toast id='post-balance-activity-toast' />
      <Toast id='application-toast' />
      <Modal
        isOpen={isRejectApplication}
        onClose={() => setIsRejectApplication(false)}
        title={'Declinar o solicitar cambios'}
        primaryText='Solicitar cambios'
        primaryVariant='primary'
        secondaryText='Declinar solicitud'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.rejected.label);
        }}
        onPrimaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.pending.label);
        }}
        children={renderRejectModal()}
      />
      <Card
        display='flex'
        margin={{ bottom: '2', top: '4' }}
        justifyContent='space-between'
        alignItems='center'
      >
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Ventas posteriores al último balance
        </P>
        {showCloudSearch ? (
          <Card width='230px'>
            <Button
              id='post-balance-activity-button-cloud'
              variant='secondary'
              onClick={fetchPostBalanceItems}
              icon='cloud_outline'
              iconAlign='right'
              fullWidth
              size='lg'
              disabled={isInCommittee || isCloudLoading}
            >
              {isCloudLoading ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Buscar en la nube'
              )}
            </Button>
          </Card>
        ) : null}
      </Card>
      <PostBalanceSheet
        onChangeSheet={(e) => handleSheetData(e, 0)}
        subtitle='Monto de ventas'
        totalText='Total ventas'
        validateForms={validateForms}
        cloudData={cloudData.sales ?? {}}
        localData={postBalanceData.sales}
        disableFields={isInCommittee}
        showAllErrors={!canSubmit}
        balanceLastMonth={{
          to_year: balanceLastMonth?.to_year ?? fetchedBalancePeriod?.to_year,
          to_month: balanceLastMonth?.to_month ?? fetchedBalancePeriod?.to_month,
        }}
        inputMarginTop='8px'
      />
      <Card
        display='flex'
        margin={{ bottom: '2', top: '4' }}
        justifyContent='space-between'
        alignItems='center'
      >
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Compras posteriores al último balance
        </P>
      </Card>
      <PostBalanceSheet
        onChangeSheet={(e) => handleSheetData(e, 1)}
        subtitle='Monto de compras'
        totalText='Total compras'
        validateForms={validateForms}
        cloudData={cloudData.purchases ?? {}}
        localData={postBalanceData.purchases}
        disableFields={isInCommittee}
        showAllErrors={!canSubmit}
        balanceLastMonth={{
          to_year: balanceLastMonth?.to_year ?? fetchedBalancePeriod?.to_year,
          to_month: balanceLastMonth?.to_month ?? fetchedBalancePeriod?.to_month,
        }}
        inputMarginTop='8px'
      />
      <Card display='flex' justifyContent='space-between' margin={{ top: '5' }}>
        {canTransitionRisk && (
          <Card width='280px'>
            <Button
              id='checkRisk-button-reject'
              size='lg'
              variant='secondary'
              onClick={() => setIsRejectApplication(true)}
              fullWidth
              disabled={isSendingApplication}
            >
              {isSendingApplication ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Declinar o solicitar cambios'
              )}
            </Button>
          </Card>
        )}
        <Card
          display='flex'
          justifyContent='flex-end'
          gap='16px'
          width={!isInCommittee ? '420px' : '210px'}
        >
          {!isInCommittee && (
            <Button
              disabled={!canSubmit}
              id='client-button-save'
              size='lg'
              variant='secondary'
              fullWidth
              onClick={() => {
                saveData(false);
              }}
            >
              {isSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
            </Button>
          )}
          <Button
            disabled={!canSubmit}
            id='client-button-save'
            size='lg'
            variant='primary'
            fullWidth
            onClick={() => {
              saveData(true);
            }}
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Continuar'}
          </Button>
        </Card>
      </Card>
    </Card>
  );
};

export default PostBalanceActivity;